<template>
  <div class="md:w-368 mx-auto">
    <JobPostScheduleCard
      id="schedule-type-regular"
      title="Regular sitter"
      body="Weekly, before/after school, driving"
      :shadow="false"
      class="mb-16"
    >
      <CalendarIconRecurring class="mx-16 my-24" color="#FF0064" />
    </JobPostScheduleCard>

    <Card class="w-full md:p-24">
      <form>
        <div class="mb-32">
          <poppins-date-picker
            id="regular-schedule-datepicker"
            v-model="startDate"
            labelName="Start date"
            :minDate="minStartDate"
            :maxDate="maxStartDate"
            :status="datepickerStatus"
            :helperText="errors.startDate"
            class="mb-16"
            @input="clearError('startDate')"
          />
          <Checkbox v-model="startDateFlexible" labelName="Start date is flexible" checkboxId="start-date-flexible" />

          <fieldset>
            <legend id="days" class="text-strong-body">Days</legend>
            <day-tokens v-model="days" class="mb-32" :status="daysStatus" :helperText="errors.days" @input="clearError('days')" />

            <poppins-time-picker
              id="regular-schedule-start"
              v-model="startTime"
              class="mb-16"
              labelName="Start time"
              :start="startRange[0]"
              :end="startRange[1]"
              :status="startTimeStatus"
              :helperText="errors.startTime"
              @input="clearError('startTime')"
            />
            <poppins-time-picker
              id="regular-schedule-end"
              v-model="endTime"
              class="mb-16"
              labelName="End time"
              :start="endRange[0]"
              :end="endRange[1]"
              :status="endTimeStatus"
              :helperText="errors.endTime"
              @input="clearError('endTime')"
            />
            <div>
              <p>Duration: {{ duration }}</p>
            </div>
          </fieldset>
        </div>
        <div class="flex flex-row-reverse">
          <RouteStepperNextButton
            :stepData="reviewSection"
            :draftData="draftData"
            :validate="validate"
            @errors="errors = $event"
          />
        </div>
      </form>
    </Card>
  </div>
</template>

<script lang="ts">
import { add, set, intervalToDuration, startOfDay, endOfDay, addHours, addDays, format, formatISO } from 'date-fns';
import Checkbox from '@/components/checkbox/Checkbox.vue';
import CalendarIconRecurring from '@/graphics/icons/CalendarIconRecurring.vue';
import JobPostScheduleCard from './JobPostScheduleCard.vue';
import PoppinsDatePicker from '@/components/PoppinsDatePicker/PoppinsDatePicker.vue';
import PoppinsTimePicker from '@/components/timepicker/PoppinsTimePicker.vue';
import DayTokens from '@/components/day_tokens/DayTokens.vue';
import { duration as calculateDuration, minStartDate, maxStartDate } from './schedule_helpers';
import Card from '@/components/card/Card.vue';
import { ReviewSection } from '@/components/route_stepper/types';
import JobPostScheduleRegularReview from './JobPostScheduleRegularReview.vue';
import { DaysOfWeek, RegularJobSchedule } from '@/store/modules/job/types';
import { JobPostDraft } from '../types';
import { HelperStatus } from '@/helpers/status';
import RouteStepperNextButton from '../../../components/route_stepper/RouteStepperNextButton.vue';

interface Errors {
  startDate?: string;
  startTime?: string;
  endTime?: string;
  days?: string;
}

interface JobPostScheduleRegularData {
  startDateFlexible: boolean;
  startDate: Date | null;
  startTime: Date | null;
  endTime: Date | null;
  startRange: Date[];
  endRange: Date[];
  days: string[];
  errors: Errors;
}

export default {
  name: 'JobPostScheduleRegular',
  components: {
    PoppinsDatePicker,
    CalendarIconRecurring,
    JobPostScheduleCard,
    Checkbox,
    PoppinsTimePicker,
    DayTokens,
    Card,
    RouteStepperNextButton,
  },
  data(): JobPostScheduleRegularData {
    return {
      startDateFlexible: false,
      startDate: null,
      startTime: null,
      endTime: null,
      startRange: [],
      endRange: [],
      days: [],
      errors: {},
    };
  },
  computed: {
    minStartDate,
    maxStartDate,
    duration(): string {
      if (this.startTime && this.endTime) {
        return calculateDuration(this.startTime, this.endTime);
      }

      return '';
    },
    datepickerStatus(): HelperStatus {
      if (this.errors.startDate) {
        return HelperStatus.Error;
      }
      return HelperStatus.Neutral;
    },
    startTimeStatus(): HelperStatus {
      if (this.errors.startTime) {
        return HelperStatus.Error;
      }
      return HelperStatus.Neutral;
    },
    endTimeStatus(): HelperStatus {
      if (this.errors.endTime) {
        return HelperStatus.Error;
      }
      return HelperStatus.Neutral;
    },
    daysStatus(): HelperStatus {
      if (this.errors.days) {
        return HelperStatus.Error;
      }
      return HelperStatus.Neutral;
    },
    noStartDateError(): boolean {
      return !this.startDate;
    },
    noDaysError(): boolean {
      return !this.days || this.days.length === 0;
    },
    noStartTimeError(): boolean {
      return !this.startTime;
    },
    noEndTimeError(): boolean {
      return !this.endTime;
    },
    endTimeBeforeStartError(): boolean {
      if (this.startTime && this.endTime) {
        return this.endTime < this.startTime;
      }
      return false;
    },
    daysOfWeekAbbr(): string[] {
      const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      return daysOfWeek.filter((_, index) => this.days.includes(String(index)));
    },
    daysOfWeekInput(): DaysOfWeek[] {
      const daysOfWeek: DaysOfWeek[] = [
        DaysOfWeek.SUNDAY,
        DaysOfWeek.MONDAY,
        DaysOfWeek.TUESDAY,
        DaysOfWeek.WEDNESDAY,
        DaysOfWeek.THURSDAY,
        DaysOfWeek.FRIDAY,
        DaysOfWeek.SATURDAY,
      ];
      return daysOfWeek.filter((_, index) => this.days.includes(String(index)));
    },
    reviewSection(): ReviewSection | undefined {
      if (!this.startDate || !this.startTime || !this.endTime || this.noDaysError) {
        return undefined;
      }

      return {
        title: 'Schedule',
        component: JobPostScheduleRegularReview,
        props: {
          formattedStartDate: format(this.startDate, 'eeee, MMMM d, yyyy'),
          startDateFlexible: this.startDateFlexible,
          startTime: this.startTime,
          endTime: this.endTime,
          days: this.daysOfWeekAbbr,
        },
        editPath: this.$route.path,
      };
    },
    regularJobSchedule(): RegularJobSchedule | undefined {
      if (!this.startDate || !this.startTime || !this.endTime) {
        return undefined;
      }

      return {
        startDate: formatISO(this.startDate, { representation: 'date' }),
        startTime: format(this.startTime, 'HH:mm'),
        endTime: format(this.endTime, 'HH:mm'),
        days: this.daysOfWeekInput,
        isStartDateFlexible: this.startDateFlexible,
        isStartTimeFlexible: false,
      };
    },
    draftData(): JobPostDraft {
      return {
        schedule: { regularJobSchedule: this.regularJobSchedule },
        careType: 'BABYSITTER',
      };
    },
  },
  watch: {
    startDate(day: Date) {
      if (this.startTime) {
        const oldStart = this.startTime;
        this.startTime = set(this.startTime, {
          year: day.getFullYear(),
          month: day.getMonth(),
          date: day.getDate(),
        });
        if (this.endTime) {
          const diff = intervalToDuration({ start: oldStart, end: this.endTime });
          this.endTime = add(this.startTime, diff);
        }
      }
      this.calculateStartRange();
    },
    startTime(newValue: Date, oldValue) {
      if (newValue && newValue !== oldValue) {
        this.calculateEndRange();
      }
    },
  },
  created() {
    this.calculateStartRange();
    this.calculateEndRange();
  },
  methods: {
    clearError(key: keyof Errors) {
      const { [key]: _removed, ...rest } = this.errors;
      this.errors = rest;
    },
    calculateStartRange() {
      let d = new Date();
      if (this.startDate) {
        d = this.startDate;
      }

      const s = startOfDay(d);
      const e = endOfDay(d);

      this.startRange = [s, e];
    },
    calculateEndRange() {
      if (!this.startTime) {
        this.endRange = [];
        return;
      }

      if (!this.endTime || this.startTime < this.endTime) {
        const s = addHours(this.startTime, 1);
        const e = addDays(this.startTime, 1);

        this.endRange = [s, e];
      }
    },
    validate(): Errors {
      const errors: Errors = {};

      if (!this.startDate) {
        errors.startDate = 'This field is required.';
      }
      if (!this.startTime) {
        errors.startTime = 'This field is required.';
      }
      if (!this.endTime) {
        errors.endTime = 'This field is required.';
      }
      if (this.endTime && this.startTime && this.endTime < this.startTime) {
        errors.endTime = 'Choose an end time later than the start time.';
      }
      if (!this.days || this.days.length === 0) {
        errors.days = 'Selecting at least one day is required.';
      }
      return errors;
    },
  },
};
</script>
